import { Injectable, OnDestroy } from '@angular/core';
import { v4 as uuid } from 'uuid';
import * as JSZip from 'jszip';
import { JSZipFileOptions } from 'jszip';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  debounceTime,
  defaultIfEmpty,
  filter,
  firstValueFrom,
  from,
  groupBy,
  map,
  mergeMap,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
  toArray,
  windowTime
} from 'rxjs';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import * as Sentry from '@sentry/angular';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { finalize, take, timeout } from 'rxjs/operators';
import { Order, OrderRegistry } from '../dto/order.dto';
import {
  BackupActions,
  BackupActionsInterface,
  OrderInEditStatusInterface,
  tableOrdersInEditStatus
} from '../db-description';
import { ErrorToastService } from './error-toast.service';
import { BackupApiService } from './backup-api.service';
import { RtsLoadingData, RtsVehicle } from '../dto/get-rts-result.dto';

export interface ConflictVersionModalData {
  localOrder: Order;
  localVersion: number;
  serverVersion: number;
}

interface ZipArchiveMetadata {
  version: number;
}

interface BackupActionData {
  order: Order;
  localVersion: number;
  idempotencyKey: string;
  overwrite: boolean;
}

export interface BackupActionResult {
  sent: boolean;
  serverOk?: boolean;
  serverVersion?: number;
  error?: unknown;
}

// Версия 3: полный РТС хранится в архиве ОТКРЫТЫМ файлом fullData.json (ранее — зашифрованный
// encryptData). Клиентское шифрование убрано, защита ПДн — на сервере at-rest.
const ZIP_ARCHIVE_CURRENT_VERSION: number = 3;
const METADATA_FILE: string = 'metadata.json';
/**
 * Страховочный предел ожидания результата исходящего действия. Должен покрывать debounce отправки (5 с)
 * и сам сетевой запрос с запасом. Нужен, чтобы освободить ключ из inFlightActionKeys, даже если
 * result-поток не эмитит (редкий рассинхрон debounceTime/windowTime в createBackupActionSubject): иначе
 * ключ застрял бы навсегда и подавил встроенный повтор действия. По таймауту запись НЕ удаляется
 * (delete идёт только после подтверждённой отправки), поэтому следующий прогон переставит её заново.
 */
const BACKUP_ACTION_TIMEOUT_MS: number = 30000;

/**
 * Резервное копирование и синхронизация полного реестра ТС заявки (бэкап) — сердце офлайн-контура и
 * восстановления данных на устройстве. Бэкап упаковывается в архив ({@code fullData.json} с ПДн +
 * {@code openData.json} без ПДн + метаданные), выгружается на forwarding-service ({@code backup-api})
 * и защищается at-rest на сервере; клиент архив не шифрует.
 *
 * Синхронизация двусторонняя и версионированная (поле {@code orderBackupVersion} записи заказа):
 * сравнение локальной и серверной версий даёт направление — выгрузить локальное ({@code SYNC_FROM_CLIENT}),
 * подтянуть серверное ({@code SYNC_FROM_SERVER}) или показать конфликт (пользователь выбирает, что
 * оставить → {@code OVERWRITE_SERVER}). Действия складываются в {@code BackupActionsTable} и выполняются
 * порядково ({@link #runAllBackupActions}).
 *
 * Инварианты сохранности (подробности — в комментариях к методам):
 * - НЕОТПРАВЛЕННЫЕ исходящие ({@code SYNC_FROM_CLIENT}/{@code OVERWRITE_SERVER}) никогда не удаляются
 *   как побочный эффект применения серверной версии и не подтягиваются поверх (иначе потеря правок
 *   реестра — водители/машины);
 * - удаление записи действия — ТОЛЬКО после подтверждённой отправки;
 * - идемпотентность выгрузки — по ключу в URL (см. {@code backup-api.sendBackup}).
 */
@Injectable({
  providedIn: 'root'
})
export class BackupService implements OnDestroy {
  public readonly orderSynced$: Subject<{ orderId: number } | number> = new Subject<{ orderId: number } | number>();
  public zip = new (JSZip as unknown as { default: new () => JSZip }).default();
  public email: string;
  public currentSecretCode$: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  public versionConflictModalTrigger$: Subject<ConflictVersionModalData> =
    new Subject<ConflictVersionModalData>();

  private stopRunAllActionsSubject = new Subject<void>();

  private readonly backupActionRequestForOrder$: Map<
    number,
    Subject<BackupActionData>
  > = new Map<number, Subject<BackupActionData>>();

  private readonly backupActionResultForOrder$: Map<
    BackupActionData,
    Subject<boolean>
  > = new Map<BackupActionData, Subject<boolean>>();

  private readonly actionResultByKey$: Map<string, Subject<BackupActionResult>> =
    new Map<string, Subject<BackupActionResult>>();

  /**
   * Ключи идемпотентности исходящих действий, которые СЕЙЧАС в обработке (между постановкой в очередь
   * и подтверждением отправки). Защита от повторной постановки того же действия при конкурентном
   * executeBackupActions в окне debounce: иначе поток отбраковки (excluded) завершил бы общий Subject
   * результата неуспехом и преждевременно удалил бы исходящую запись (SYNC_FROM_CLIENT/OVERWRITE_SERVER)
   * ещё до подтверждённой отправки — потеря выбранной пользователем версии реестра.
   */
  private readonly inFlightActionKeys: Set<string> = new Set<string>();

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private dbService: NgxIndexedDBService,
    private errorToastService: ErrorToastService,
    private backupApiService: BackupApiService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Освобождаем накопленное состояние незавершённых действий (на случай застрявших ключей).
    this.inFlightActionKeys.clear();
    this.actionResultByKey$.forEach(subject => subject.complete());
    this.actionResultByKey$.clear();
  }

  /**
   * Проверяем клиент на наличие несинхронизированных и устаревших бекапов.
   * Получает список бекапов с сервера и сравнивает их версии с локальными.
   * @returns {Promise<void>} Promise, который разрешается после завершения проверки.
   */
  async checkUpdates(): Promise<void> {
    try {
      const serverBackupsData = await firstValueFrom(
        this.backupApiService.getAllBackupListWithVersion()
      );

      if (serverBackupsData) {
        from(serverBackupsData.data.backupVersions)
          .pipe(
            concatMap(backupRecord => {
              const serverVersion = backupRecord.version;

              return this.getOrderDataFromLocalStore(backupRecord.id).pipe(
                concatMap(
                  (existingRecord: OrderInEditStatusInterface | undefined) => {
                    if (existingRecord) {
                      return this.compareBackupVersion(
                        backupRecord.id,
                        serverVersion,
                        Number(existingRecord.orderBackupVersion),
                        existingRecord.Order
                      );
                    }
                    return this.compareBackupVersion(
                      backupRecord.id,
                      serverVersion,
                      0
                    );
                  }
                )
              );
            }),
            toArray()
          )
          .subscribe({
            next: () => {
              this.executeBackupActions();
            },
            error: error => {
              Sentry.captureException(error);
            }
          });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  async checkUpdatesFromServerList(backupVersions: { id: number; version: number }[]): Promise<void> {
    try {
      await firstValueFrom(
        from(backupVersions).pipe(
          concatMap(({ id, version }) =>
            this.getOrderDataFromLocalStore(id).pipe(
              concatMap((existingRecord: OrderInEditStatusInterface | undefined) => {
                if (existingRecord) {
                  return this.compareBackupVersion(
                    id,
                    version,
                    Number(existingRecord.orderBackupVersion),
                    existingRecord.Order
                  );
                }
                return this.compareBackupVersion(id, version, 0);
              })
            )
          ),
          toArray(),
          tap(() => {
            void this.executeBackupActions();
          })
        )
      );
    } catch (error) {
      Sentry.captureException(error, { extra: { operation: 'checkUpdatesFromServerList' } });
    }
  }

  /**
   * Удаляет устаревшие записи о бекапах, которых больше нет на сервере.
   * @param {number[]} serverBackupIds - Массив ID бекапов, которые есть на сервере.
   * @returns {Promise<number>} Количество удаленных записей.
   */
  async deleteOutdatedBackups(serverBackupIds: number[]): Promise<number> {
    const currentUser = localStorage.getItem('login');
    if (!currentUser) {
      return 0;
    }

    const storeName: string = 'BackupActionsTable';

    try {
      return await firstValueFrom(
        this.dbService
          .getAllByIndex<BackupActionsInterface>(
            storeName,
            'user',
            IDBKeyRange.only(currentUser)
          )
          .pipe(
            switchMap(records => {
              type RecordKey = [string, number, number];
              // Удаляем ТОЛЬКО входящие кэши (SYNC_FROM_SERVER) заявок, которых больше нет в серверном
              // списке — они безопасно перезагружаемы с сервера. Исходящие несинхронизированные записи
              // (SYNC_FROM_CLIENT — выгрузка локальных правок реестра; OVERWRITE_SERVER — «оставить
              // локальную версию» из разрешения конфликта) НИКОГДА не трогаем: их нет на сервере именно
              // потому, что они ещё не выгружены, и их удаление означало бы безвозвратную потерю
              // юридически значимых данных реестра (водители/машины).
              const keys: RecordKey[] = records
                .filter(
                  record =>
                    record.backupAction === BackupActions.SYNC_FROM_SERVER &&
                    !serverBackupIds.includes(record.Id)
                )
                .map(
                  record =>
                    [
                      record.user,
                      record.Id,
                      Number(record.orderBackupVersion)
                    ] as RecordKey
                );

              if (keys.length > 0) {
                return this.dbService
                  .bulkDelete(storeName, keys)
                  .pipe(map(() => keys.length));
              }
              return of(0);
            }),
            catchError(error => {
              Sentry.captureException(error, {
                extra: {
                  operation: 'deleteOutdatedBackups',
                  serverBackupIds
                }
              });
              return of(0);
            })
          )
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'deleteOutdatedBackups',
          serverBackupIds
        }
      });
      return 0;
    }
  }

  /**
   * Сравнивает версии бекапа на сервере и локально, определяет необходимое действие.
   *
   * @param {number} id - Идентификатор заказа.
   * @param {number} serverVersion - Версия бекапа на сервере.
   * @param {number} localVersion - Версия бекапа локально.
   * @param {Order} [localOrder] - Локальные данные заказа (если есть).
   * @returns {Promise<BackupActionsInterface | null>} Promise с результатом операции или null, если действие не требуется.
   */
  async compareBackupVersion(
    id: number,
    serverVersion: number,
    localVersion: number,
    localOrder?: Order
  ): Promise<BackupActionsInterface | null> {
    try {
      const idempotencyKey = uuid();

      // Если локальной версии нет, синхронизируем с сервера. Но авто-подтяжка серверной версии
      // перезапишет локальный заказ и удалит записи действий ≤ версии (тип-слепо), включая
      // НЕОТПРАВЛЕННЫЕ исходящие. Поэтому, если для заказа есть ожидающее исходящее действие,
      // подтяжку пропускаем — молча затирать неотправленные правки реестра нельзя. Сюда localVersion=0
      // приходит и при отсутствии записи заказа, и при СБОЕ её чтения (getOrderDataFromLocalStore
      // глотает ошибку в undefined), поэтому защита нужна на едином корне для всех вызывающих.
      // Разрешение конфликта «взять серверную» идёт мимо этого метода (writeBackupAction напрямую из
      // модалки), поэтому оно не затрагивается и проигравшую локальную версию по-прежнему удаляет.
      if (localVersion === 0) {
        if (await this.hasPendingOutgoingAction(id)) {
          return null;
        }
        return await firstValueFrom(
          this.writeBackupAction(
            id,
            serverVersion,
            BackupActions.SYNC_FROM_SERVER,
            idempotencyKey
          )
        );
      }

      // Если локальная версия новее, отправляем на сервер
      if (serverVersion < localVersion) {
        return await firstValueFrom(
          this.writeBackupAction(
            id,
            localVersion,
            BackupActions.SYNC_FROM_CLIENT,
            idempotencyKey,
            localOrder
          )
        );
      }

      // Если версии совпадают, ничего не делаем
      if (serverVersion === localVersion) {
        return null;
      }

      // Если серверная версия новее и есть локальные данные, показываем конфликт
      if (serverVersion > localVersion && localOrder) {
        this.versionConflictModalTrigger$.next({
          localOrder,
          localVersion,
          serverVersion
        });
      }
      return null;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'compareBackupVersion',
          id,
          serverVersion,
          localVersion
        }
      });
      throw error;
    }
  }

  /**
   * Получает запись заказа из локального хранилища по его ID.
   *
   * @param {number} orderId - Идентификатор заказа.
   * @returns {Observable<OrderInEditStatusInterface | undefined>} Observable с данными заказа или undefined, если запись не найдена.
   */
  getOrderDataFromLocalStore(
    orderId: number
  ): Observable<OrderInEditStatusInterface | undefined> {
    return this.dbService
      .getByKey<OrderInEditStatusInterface>(tableOrdersInEditStatus, orderId)
      .pipe(
        catchError(error => {
          Sentry.captureException(error, {
            extra: {
              operation: 'getOrderDataFromLocalStore',
              orderId
            }
          });
          return of(undefined);
        })
      );
  }

  /**
   * Получает бекап с сервера и записывает его в локальное хранилище.
   *
   * @param {number} orderId - Идентификатор заказа.
   * @returns {Observable<OrderInEditStatusInterface | null>} Observable с данными заказа или null, если произошла ошибка.
   */
  writeServerBackupToLocalStore(
    orderId: number
  ): Observable<OrderInEditStatusInterface | null> {
    const currentLogin = localStorage.getItem('login');
    if (!currentLogin) {
      Sentry.captureMessage('Попытка записи бекапа без авторизации', {
        level: 'warning',
        extra: { orderId }
      });
      return of(null);
    }

    return this.backupApiService.getBackupById(orderId).pipe(
      concatMap(backupBlob => {
        const newVersion = backupBlob.headers.get('Backup-Version');
        if (!newVersion) {
          Sentry.captureMessage('Получен бекап без версии', {
            level: 'warning',
            extra: { orderId }
          });
          return of(null);
        }

        return from(this.extractOrderFromZip(backupBlob.body)).pipe(
          concatMap(decryptedData => {
            if (decryptedData) {
              return this.getOrderDataFromLocalStore(orderId).pipe(
                concatMap(
                  (existingRecord: OrderInEditStatusInterface | undefined) => {
                    if (existingRecord) {
                      if (!existingRecord.user) {
                        existingRecord.user = currentLogin;
                      }

                      existingRecord.Order = decryptedData;
                      existingRecord.orderBackupVersion = Number(newVersion);
                      return this.dbService.update(
                        tableOrdersInEditStatus,
                        existingRecord
                      ).pipe(
                        tap(() => {
                          try {
                            this.orderSynced$.next({ orderId });
                          } catch (e) {
                            Sentry.captureException(e, { extra: { operation: 'orderSynced$.next', orderId } });
                          }
                        })
                      );
                    }
                    const newRecord: OrderInEditStatusInterface = {
                      Id: orderId,
                      Order: decryptedData,
                      orderBackupVersion: Number(newVersion),
                      user: currentLogin
                    };
                    return this.dbService.add(
                      tableOrdersInEditStatus,
                      newRecord
                    ).pipe(
                      tap(() => {
                        try {
                          this.orderSynced$.next({ orderId });
                        } catch (e) {
                          Sentry.captureException(e, { extra: { operation: 'orderSynced$.next', orderId } });
                        }
                      })
                    );
                  }
                ),
                catchError(error => {
                  Sentry.captureException(error, {
                    extra: {
                      operation: 'writeServerBackupToLocalStore',
                      orderId,
                      newVersion
                    }
                  });
                  return of(null);
                })
              );
            }
            return of(null);
          })
        );
      }),
      catchError(error => {
        Sentry.captureException(error, {
          extra: {
            operation: 'writeServerBackupToLocalStore',
            orderId
          }
        });
        return of(null);
      })
    );
  }

  /**
   * Извлекает заказ из архива бекапа: базовый Order из openData.json, реестр РТС — из открытого
   * fullData.json (замещает обезличенный реестр реальными данными с ПДн). Расшифровки нет — клиент
   * архив не шифрует.
   *
   * @param {Blob} r - Бинарные данные архива.
   * @returns {Promise<Order | null>} Promise с данными заказа или null, если произошла ошибка.
   */
  async extractOrderFromZip(r: Blob): Promise<Order | null> {
    try {
      this.zip = new (JSZip as unknown as { default: new () => JSZip }).default();
      const zip = await this.zip.loadAsync(r);

      // Проверяем наличие файла с открытыми данными
      const originalFile = zip.file('openData.json');
      if (!originalFile) {
        Sentry.captureMessage('Файл openData.json не найден в архиве', {
          level: 'error'
        });
        return null;
      }

      // Парсим открытые данные
      const openDataText = await originalFile.async('string');
      let order: Order;
      try {
        order = JSON.parse(openDataText) as Order;
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            operation: 'extractOrderFromZip',
            step: 'parse openData.json'
          }
        });
        return null;
      }

      // Читаем полный РТС из открытого файла fullData.json. Он замещает обезличенный реестр из
      // openData.json реальными данными (с ПДн). Клиент архив не расшифровывает — полный РТС лежит
      // открытым (защита ПДн обеспечивается сервером at-rest).
      const fullDataFile = zip.file('fullData.json');
      if (!fullDataFile) {
        // Файла нет → заказ НЕ восстанавливаем (возвращаем null), чтобы обезличенный реестр из
        // openData не ушёл наружу и не затёр реальные данные на сервере при обратной синхронизации.
        Sentry.captureMessage('Файл fullData.json не найден в архиве', {
          level: 'error',
          extra: { orderId: order?.id }
        });
        return null;
      }

      const fullDataText = await fullDataFile.async('string');
      try {
        const registry = JSON.parse(fullDataText) as Order['registry'];
        if (!registry) {
          return null;
        }
        order.registry = registry;
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            operation: 'extractOrderFromZip',
            step: 'parse fullData.json'
          }
        });
        return null;
      }

      return order;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'extractOrderFromZip'
        }
      });
      return null;
    }
  }

  /**
   * Сохраняет бекап заказа на сервер.
   * Получает данные заказа из локального хранилища и создает задачу на синхронизацию.
   *
   * @param {number} id - Идентификатор заказа.
   * @returns {Promise<void>} Promise, который разрешается после создания задачи на синхронизацию.
   */
  async saveBackup(id: number): Promise<void> {
    try {
      return new Promise<void>((resolve, reject) => {
        this.getOrderDataFromLocalStore(id)
          .pipe(
            take(1),
            catchError(error => {
              Sentry.captureException(error, {
                extra: {
                  operation: 'saveBackup',
                  id
                }
              });
              this.errorToastService.showError(
                'Ошибка при получении данных заказа'
              );
              reject(error);
              return of(undefined);
            })
          )
          .subscribe({
            next: (existingRecord: OrderInEditStatusInterface | undefined) => {
              if (!existingRecord) {
                const error = new Error(
                  `Заказ с ID ${id} не найден в локальном хранилище`
                );
                Sentry.captureException(error, {
                  level: 'warning',
                  extra: {
                    operation: 'saveBackup',
                    id
                  }
                });
                this.errorToastService.showError(
                  'Заказ не найден в локальном хранилище'
                );
                reject(error);
                return;
              }

              if (existingRecord.orderBackupVersion === 0) {
                resolve();
                return;
              }
              const daysArr = (existingRecord.Order?.registry?.days ?? []) as {
                vehicles?: unknown[];
                started?: boolean;
              }[];
              const hasMeaningful = daysArr.some(
                d => Array.isArray(d.vehicles) && d.vehicles.length > 0
              );
              // «В работе» = хотя бы один день начат экспедитором. Пустой реестр синхронизируем ТОЛЬКО
              // когда он в работе: тогда отсутствие машин означает НАМЕРЕННОЕ удаление всех ТС, и сервер
              // должен это отразить. Незагруженный или ещё не начатый реестр (первичный РТС, в т.ч. дни
              // с пустым списком ТС от логиста) НЕ проталкиваем — иначе затёрли бы серверные данные
              // пустотой до того, как экспедитор приступил к работе.
              const registryInWork = daysArr.some(d => d.started === true);
              if (!hasMeaningful && !registryInWork) {
                Sentry.captureMessage('Остановлен пустой бэкап нерабочего реестра', {
                  level: 'info',
                  extra: { operation: 'saveBackup', id, version: existingRecord.orderBackupVersion }
                });
                resolve();
                return;
              }

              const idempotencyKey = uuid();
              this.writeBackupAction(
                existingRecord.Order.id,
                existingRecord.orderBackupVersion,
                BackupActions.SYNC_FROM_CLIENT,
                idempotencyKey,
                existingRecord.Order
              )
                .pipe(
                  take(1),
                  catchError(error => {
                    Sentry.captureException(error, {
                      extra: {
                        operation: 'saveBackup.writeBackupAction',
                        id,
                        version: existingRecord.orderBackupVersion
                      }
                    });
                    this.errorToastService.showError(
                      'Ошибка при создании задачи на синхронизацию'
                    );
                    reject(error);
                    return of(null);
                  })
                )
                .subscribe({
                  next: () => {
                    this.executeBackupActions()
                      .then(() => resolve())
                      .catch(error => {
                        Sentry.captureException(error, {
                          extra: {
                            operation: 'saveBackup.executeBackupActions',
                            id
                          }
                        });
                        this.errorToastService.showError(
                          'Ошибка при выполнении синхронизации'
                        );
                        reject(error);
                      });
                  },
                  error: error => {
                    Sentry.captureException(error, {
                      extra: {
                        operation: 'saveBackup.writeBackupAction.subscribe',
                        id
                      }
                    });
                    this.errorToastService.showError(
                      'Ошибка при создании задачи на синхронизацию'
                    );
                    reject(error);
                  }
                });
            },
            error: error => {
              Sentry.captureException(error, {
                extra: {
                  operation: 'saveBackup.getOrderDataFromLocalStore.subscribe',
                  id
                }
              });
              this.errorToastService.showError(
                'Ошибка при получении данных заказа'
              );
              reject(error);
            }
          });
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'saveBackup',
          id
        }
      });
      this.errorToastService.showError(
        'Непредвиденная ошибка при сохранении бекапа'
      );
      throw error;
    }
  }

  /**
   * Подготавливает данные заказа для открытого хранения, удаляя или редактируя конфиденциальную информацию.
   * Создает копию заказа с редактированием персональных данных водителей и перевозчиков.
   *
   * @param {Order} order - Исходные данные заказа.
   * @returns {Order} Копия заказа с отредактированными конфиденциальными данными.
   */
  parseOpenData(order: Order): Order {
    try {
      // Создаем глубокую копию объекта заказа
      const clonedOrder = JSON.parse(JSON.stringify(order)) as Order;

      if (clonedOrder.registry && clonedOrder.registry.days) {
        clonedOrder.registry.days.forEach(day => {
          if (Array.isArray(day.vehicles)) {
            day.vehicles = day.vehicles.map(
              ({ id, loadingData = {} as RtsLoadingData, ...v }) => {
                // Функция для безопасного редактирования данных
                const redactOrDefault = <T>(
                  value: T | null | undefined,
                  redactFn: (input: T) => string
                ): string | null =>
                  value != null ? redactFn(value as T) : null;

                // Возвращаем объект с отредактированными персональными данными
                return {
                  id,
                  loadingData,
                  ...v,
                  // Удаляем конфиденциальные данные
                  carrierPassportNumber: '',
                  carrierPassportDate: '',
                  carrierPassportDetails: '',
                  carrierAddress: '',
                  // Редактируем данные, которые могут быть полезны в UI
                  carrierEmail: redactOrDefault(
                    v.carrierEmail,
                    this.redactEmail.bind(this)
                  ),
                  driverName: redactOrDefault(
                    v.driverName,
                    this.redactName.bind(this)
                  ),
                  driverPassport: '',
                  driverLicenseNumber: '',
                  driverPhone: redactOrDefault(
                    v.driverPhone,
                    this.redactPhone.bind(this)
                  ),
                  driverInn: ''
                };
              }
            ) as RtsVehicle[];
          }
        });
      }

      return clonedOrder;
    } catch (error) {
      // В случае ошибки логируем и возвращаем исходный объект
      Sentry.captureException(error, {
        extra: {
          operation: 'parseOpenData',
          orderId: order?.id
        }
      });
      return order;
    }
  }

  /**
   * Отправляет данные заказа из локального хранилища на сервер.
   * Шифрует конфиденциальные данные, упаковывает в архив и отправляет на сервер.
   *
   * @param {Order} order - Данные заказа для отправки.
   * @param {number} localVersion - Локальная версия бекапа.
   * @param {string} idempotencyKey - Ключ идемпотентности для предотвращения дублирования запросов.
   * @param {boolean} overwrite - Флаг, указывающий, нужно ли перезаписать данные на сервере.
   * @returns {Observable<HttpResponse<void> | null>} Observable с ответом сервера или null в случае ошибки.
   */
  writeLocalBackupToServer(
    order: Order,
    localVersion: number,
    idempotencyKey: string,
    overwrite: boolean
  ): Observable<HttpResponse<void> | null> {
    if (!order || !order.id) {
      Sentry.captureMessage('Попытка отправки некорректного заказа на сервер', {
        level: 'warning',
        extra: { order }
      });
      return of(null);
    }

    // Открытые данные с замаскированными ПДн — версия «РТС без ПДн» для потребителей,
    // которым персональные данные водителя не нужны.
    const openData = this.parseOpenData(order);

    // Полный РТС (с ПДн) отправляем ОТКРЫТЫМ: клиент его больше не шифрует. Защита ПДн
    // обеспечивается на сервере (шифрование архива at-rest, ключ из config-server).
    return from(this.packToZip(order.registry, openData)).pipe(
      switchMap((zipBlob: Blob) =>
        // Отправляем архив на сервер
        this.backupApiService
          .sendBackup(
            order.id.toString(),
            zipBlob,
            localVersion,
            overwrite,
            idempotencyKey
          )
          .pipe(
            catchError((error: HttpErrorResponse) => {
              // Обрабатываем ошибку конфликта версий
              if (error.status === 412) {
                const serverVersion = Number(
                  error.headers?.get('backup-version') || 0
                );

                if (serverVersion > 0) {
                  // Показываем модальное окно с конфликтом версий
                  this.versionConflictModalTrigger$.next({
                    localOrder: order,
                    localVersion,
                    serverVersion
                  });
                  return of(null);
                }
              } else {
                // Обрабатываем другие ошибки
                Sentry.captureException(error, {
                  extra: {
                    operation: 'writeLocalBackupToServer',
                    orderId: order.id,
                    localVersion,
                    status: error.status
                  }
                });
              }
              return throwError(() => error);
            })
          )
      ),
      catchError(error => {
        Sentry.captureException(error, {
          extra: {
            operation: 'writeLocalBackupToServer.packToZip',
            orderId: order.id
          }
        });
        return of(null);
      })
    );
  }

  /**
   * Создаёт ZIP-архив бэкапа: полный РТС (открытым) + открытые данные с замаскированными ПДн +
   * метаданные версии. Шифрование архива выполняется на сервере at-rest.
   *
   * @param {OrderRegistry} fullData - Полный реестр РТС с ПДн (открытый).
   * @param {Order} openData - Заказ с замаскированными ПДн (версия «без ПДн»).
   * @returns {Promise<Blob>} Promise с бинарными данными архива.
   * @throws {Error} Если произошла ошибка при создании архива.
   */
  async packToZip(fullData: OrderRegistry, openData: Order): Promise<Blob> {
    try {
      // Создаем новый экземпляр JSZip
      this.zip = new (JSZip as unknown as { default: new () => JSZip }).default();

      // Настройки сжатия для файлов в архиве
      const zipOptions: JSZipFileOptions = {
        compression: 'DEFLATE',
        compressionOptions: { level: 5 }
      };

      // Полный РТС (с ПДн) — открытым файлом; шифрование архива выполняет сервер at-rest.
      this.zip.file('fullData.json', JSON.stringify(fullData), zipOptions);

      // Открытые данные с замаскированными ПДн (версия «без ПДн»).
      this.zip.file('openData.json', JSON.stringify(openData), zipOptions);

      // Добавляем метаданные архива с версией
      this.zip.file(
        METADATA_FILE,
        JSON.stringify({
          version: ZIP_ARCHIVE_CURRENT_VERSION
        } as ZipArchiveMetadata),
        zipOptions
      );

      // Генерируем архив в формате Blob
      return await this.zip.generateAsync({ type: 'blob' });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'packToZip',
          orderId: openData?.id
        }
      });
      throw new Error(
        `Ошибка при создании архива: ${error instanceof Error ? error.message : 'неизвестная ошибка'}`
      );
    }
  }

  /**
   * Записывает действие по синхронизации бекапа в локальное хранилище.
   * Создает или обновляет запись в таблице BackupActionsTable.
   *
   * @param {number} id - Идентификатор заказа.
   * @param {number} version - Версия бекапа.
   * @param {BackupActions} action - Тип действия (синхронизация с сервера, с клиента, перезапись).
   * @param {string} idempotencyKey - Ключ идемпотентности для предотвращения дублирования запросов.
   * @param {Order} [order] - Данные заказа (опционально).
   * @returns {Observable<BackupActionsInterface>} Observable с данными созданного/обновленного действия.
   */
  writeBackupAction(
    id: number,
    version: number,
    action: BackupActions,
    idempotencyKey: string,
    order?: Order
  ): Observable<BackupActionsInterface> {
    const email = localStorage.getItem('login');

    if (!email) {
      const error = new Error('Попытка записи действия без авторизации');
      Sentry.captureMessage(error.message, {
        level: 'warning',
        extra: { id, version, action }
      });
      return throwError(() => error);
    }

    this.email = email;

    const backupActionTableData: BackupActionsInterface = {
      user: this.email,
      Id: id,
      Order: order,
      orderBackupVersion: version,
      backupAction: action,
      idempotencyKey
    };

    return this.dbService
      .getByKey('BackupActionsTable', [this.email, id, version])
      .pipe(
        switchMap(existingRecord => {
          const currentRecord = existingRecord as BackupActionsInterface;
          if (currentRecord) {
            // Обновляем существующую запись
            return this.dbService.update('BackupActionsTable', {
              ...currentRecord,
              ...backupActionTableData
            });
          }
          // Создаем новую запись
          return this.dbService.add(
            'BackupActionsTable',
            backupActionTableData
          );
        }),
        catchError(error => {
          Sentry.captureException(error, {
            extra: {
              operation: 'writeBackupAction',
              id,
              version,
              action
            }
          });
          return throwError(
            () =>
              new Error(
                `Ошибка при записи действия в хранилище: ${error instanceof Error ? error.message : 'неизвестная ошибка'}`
              )
          );
        })
      );
  }

  /**
   * Позволяет подписаться на итог выполнения операции по idempotencyKey
   */
  waitForActionResult(idempotencyKey: string): Observable<BackupActionResult> {
    let subject = this.actionResultByKey$.get(idempotencyKey);
    if (!subject) {
      // ReplaySubject(1), а не Subject: результат может быть эмитирован ДО подписки (например, путь
      // SYNC_FROM_SERVER уведомляет напрямую, без debounce). Обычный Subject потерял бы такое значение,
      // и ожидающий (модальное окно выбора версии) завис бы навсегда.
      subject = new ReplaySubject<BackupActionResult>(1);
      this.actionResultByKey$.set(idempotencyKey, subject);
    }
    return subject.asObservable();
  }

  /**
   * Запускает все отложенные операции резервного копирования и обрабатывает результаты.
   * Выполняет синхронизацию данных между локальным хранилищем и сервером.
   *
   * @returns {Promise<void>} Promise, который разрешается после завершения всех операций.
   */
  async executeBackupActions(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.runAllBackupActions()
        .pipe(
          takeUntil(this.stopRunAllActionsSubject),
          finalize(() => {
            // Всегда вызываем resolve, даже если были ошибки,
            // так как ошибки уже обработаны внутри runAllBackupActions
            resolve();
          })
        )
        .subscribe({
          error: error => {
            Sentry.captureException(error, {
              extra: {
                operation: 'executeBackupActions'
              }
            });
            reject(error);
          }
        });
    });
  }

  /**
   * Выполняет все отложенные действия для синхронизации бекапов.
   * Получает список действий из локального хранилища и выполняет их в зависимости от типа.
   *
   * @returns {Observable<null>} Observable, который завершается после выполнения всех действий.
   */
  runAllBackupActions(): Observable<null> {
    return this.getLastVersionOfBackupActions().pipe(
      concatMap(actions => {
        if (!actions || actions.length === 0) {
          return of(null);
        }

        return from(actions).pipe(
          concatMap(action => {
            const loggedInUser = localStorage.getItem('login');

            if (!loggedInUser) {
              return of(null);
            }

            // Действие другого пользователя (возможно в узком окне гонки: 'login' сменился между снимком
            // списка в getLastVersionOfBackupActions и повторным чтением здесь). Удалять можно ТОЛЬКО
            // безопасно перезагружаемый входящий кэш (SYNC_FROM_SERVER). Исходящие несинхронизированные
            // записи (SYNC_FROM_CLIENT — выгрузка правок реестра; OVERWRITE_SERVER — «оставить локальную
            // версию» из разрешения конфликта) НИКОГДА не удаляем: их нет на сервере именно потому, что
            // ещё не выгружены, и удаление означало бы безвозвратную потерю юр.данных реестра (водители/
            // машины). Такие записи пропускаем — они переиграются, когда их владелец снова войдёт. Тот же
            // принцип, что в deleteOutdatedBackups.
            if (action.user !== loggedInUser) {
              if (action.backupAction !== BackupActions.SYNC_FROM_SERVER) {
                return of(null);
              }
              return from(
                this.deleteBackupActionsByVersion(
                  action.Id,
                  Number(action.orderBackupVersion),
                  true // побочная очистка входящего кэша — неотправленные исходящие не трогаем
                )
              ).pipe(
                catchError(error => {
                  Sentry.captureException(error, {
                    extra: {
                      operation: 'runAllBackupActions.deleteOtherUserAction',
                      actionId: action.Id,
                      actionVersion: action.orderBackupVersion
                    }
                  });
                  return of(null);
                })
              );
            }

            let operation$: Observable<unknown>;

            // Выбираем операцию в зависимости от типа действия
            switch (action.backupAction) {
              case BackupActions.SYNC_FROM_CLIENT:
                // Проверяем наличие данных заказа
                if (!action.Order) {
                  return of(null);
                }

                // Уже отправляется (постановка в очередь ещё не подтверждена) — не дублируем: повторная
                // постановка того же ключа в окне debounce привела бы к отбраковке и преждевременному
                // удалению записи до подтверждения отправки.
                if (this.inFlightActionKeys.has(action.idempotencyKey)) {
                  return of(null);
                }
                this.inFlightActionKeys.add(action.idempotencyKey);

                // Отправляем данные с клиента на сервер
                operation$ = from(
                  this.putBackupActionToQueue({
                    order: action.Order,
                    localVersion: Number(action.orderBackupVersion),
                    idempotencyKey: action.idempotencyKey,
                    overwrite: false
                  })
                ).pipe(
                  concatMap(sent =>
                    // Удаляем запись ТОЛЬКО при подтверждённой отправке. При отбраковке (excluded → false)
                    // или серверном неуспехе запись остаётся в очереди для повтора — иначе потеряли бы
                    // правки реестра, ещё не дошедшие до сервера.
                    sent
                      ? from(
                          this.deleteBackupActionsByVersion(
                            action.Id,
                            Number(action.orderBackupVersion)
                          )
                        )
                      : of(null)
                  ),
                  // Страховка: освобождаем ключ, даже если result-поток не эмитил (рассинхрон
                  // debounce/windowTime). Запись при этом не удаляется — следующий прогон переставит её.
                  timeout(BACKUP_ACTION_TIMEOUT_MS),
                  finalize(() =>
                    this.inFlightActionKeys.delete(action.idempotencyKey)
                  )
                );
                break;

              case BackupActions.OVERWRITE_SERVER:
                // Проверяем наличие данных заказа
                if (!action.Order) {
                  return of(null);
                }

                // Уже отправляется — не дублируем (см. пояснение в ветке SYNC_FROM_CLIENT). Для
                // OVERWRITE_SERVER («оставить локальную версию» из разрешения конфликта) это особенно
                // важно: преждевременное удаление означало бы потерю выбранной пользователем версии.
                if (this.inFlightActionKeys.has(action.idempotencyKey)) {
                  return of(null);
                }
                this.inFlightActionKeys.add(action.idempotencyKey);

                // Перезаписываем данные на сервере
                operation$ = from(
                  this.putBackupActionToQueue({
                    order: action.Order,
                    localVersion: Number(action.orderBackupVersion),
                    idempotencyKey: action.idempotencyKey,
                    overwrite: true
                  })
                ).pipe(
                  concatMap(sent =>
                    // Удаляем запись ТОЛЬКО при подтверждённой отправке. При отбраковке (excluded → false)
                    // или серверном неуспехе запись остаётся в очереди для повтора — иначе потеряли бы
                    // правки реестра, ещё не дошедшие до сервера.
                    sent
                      ? from(
                          this.deleteBackupActionsByVersion(
                            action.Id,
                            Number(action.orderBackupVersion)
                          )
                        )
                      : of(null)
                  ),
                  // Страховка от застревания ключа (см. пояснение в ветке SYNC_FROM_CLIENT).
                  timeout(BACKUP_ACTION_TIMEOUT_MS),
                  finalize(() =>
                    this.inFlightActionKeys.delete(action.idempotencyKey)
                  )
                );
                break;

              case BackupActions.SYNC_FROM_SERVER:
                // Получаем данные с сервера
                operation$ = from(
                  this.writeServerBackupToLocalStore(action.Id)
                ).pipe(
                  tap(result => {
                    const keySubject = this.actionResultByKey$.get(action.idempotencyKey);
                    if (keySubject) {
                      const ok = !!result; // null означает, что не удалось получить/записать бэкап
                      keySubject.next({ sent: true, serverOk: ok, serverVersion: Number(action.orderBackupVersion) });
                      keySubject.complete();
                      this.actionResultByKey$.delete(action.idempotencyKey);
                    }
                  }),
                  concatMap(() =>
                    from(
                      this.deleteBackupActionsByVersion(
                        action.Id,
                        Number(action.orderBackupVersion),
                        true // применили серверную версию — неотправленные исходящие сохраняем (B6)
                      )
                    )
                  )
                );
                break;

              default:
                operation$ = of(null);
                break;
            }

            // Обрабатываем ошибки для каждой операции
            return operation$.pipe(
              catchError(error => {
                Sentry.captureException(error, {
                  extra: {
                    operation: 'runAllBackupActions.executeAction',
                    actionId: action.Id,
                    actionVersion: action.orderBackupVersion,
                    actionType: action.backupAction
                  }
                });
                return of(null); // Продолжаем выполнение других действий
              })
            );
          })
        );
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.stopActions();
      }),
      // Всегда возвращаем null для единообразия
      map(() => null)
    );
  }

  /**
   * Удаляет действия по синхронизации бекапа из локального хранилища по ID и версии.
   * Удаляет все действия с версией меньше или равной указанной.
   *
   * @param {number} id - Идентификатор заказа.
   * @param {number} version - Максимальная версия для удаления.
   * @param {boolean} [preserveUnsentOutgoing=false] - Если {@code true}, НЕ удалять неотправленные
   *   исходящие действия ({@link BackupActions.SYNC_FROM_CLIENT} — выгрузка правок реестра;
   *   {@link BackupActions.OVERWRITE_SERVER} — «оставить локальную версию» из разрешения конфликта).
   *   Задаётся при удалении как побочном эффекте ПРИМЕНЕНИЯ серверной версии (входящий кэш): очистка
   *   по версии тип-слепа и без этого затёрла бы неотправленные правки реестра (водители/машины) —
   *   безвозвратно, так как на сервере их ещё нет. После ПОДТВЕРЖДЁННОЙ отправки исходящего оставляем
   *   {@code false}: там удаление отправленной записи и есть цель.
   * @returns {Promise<number>} Promise с количеством удаленных записей.
   */
  async deleteBackupActionsByVersion(
    id: number,
    version: number,
    preserveUnsentOutgoing = false
  ): Promise<number> {
    const email = localStorage.getItem('login');

    if (!email) {
      return 0;
    }

    try {
      return new Promise<number>((resolve, reject) => {
        this.dbService
          .getAllByIndex('BackupActionsTable', 'Id', IDBKeyRange.only(id))
          .pipe(
            mergeMap(
              (records: unknown[]) => records as BackupActionsInterface[]
            ),
            filter(
              (record: BackupActionsInterface) =>
                record.Id === id &&
                record.user === email &&
                record.orderBackupVersion <= version &&
                // Тип-осознанность (B6): при применении серверной версии не трогаем неотправленные
                // исходящие — иначе молча потеряли бы правки реестра, ещё не дошедшие до сервера.
                !(
                  preserveUnsentOutgoing &&
                  (record.backupAction === BackupActions.SYNC_FROM_CLIENT ||
                    record.backupAction === BackupActions.OVERWRITE_SERVER)
                )
            ),
            toArray(),
            catchError(error => {
              reject(error);
              return of([]);
            })
          )
          .subscribe({
            next: filteredRecords => {
              if (filteredRecords.length === 0) {
                resolve(0);
                return;
              }

              let deletedCount = 0;
              let completedCount = 0;

              // Функция для проверки завершения всех операций
              const checkCompletion = () => {
                if (completedCount === filteredRecords.length) {
                  // No need to log failed deletions
                  resolve(deletedCount);
                }
              };

              filteredRecords.forEach(recordForDelete => {
                this.dbService
                  .delete('BackupActionsTable', [
                    recordForDelete.user,
                    recordForDelete.Id,
                    recordForDelete.orderBackupVersion
                  ])
                  .pipe(
                    catchError(() => {
                      completedCount++;
                      checkCompletion();
                      return of(null);
                    })
                  )
                  .subscribe({
                    next: () => {
                      deletedCount++;
                      completedCount++;
                      checkCompletion();
                    },
                    error: () => {
                      completedCount++;
                      checkCompletion();
                    }
                  });
              });
            },
            error: error => {
              reject(error);
            }
          });
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'deleteBackupActionsByVersion',
          id,
          version
        }
      });
      return 0;
    }
  }

  /**
   * Получаем последнюю версию каждого действия синхронизации от текущего пользователя
   * @returns {Observable<BackupActionsInterface[]>} Observable массива последних версий действий бэкапа
   */
  getLastVersionOfBackupActions(): Observable<BackupActionsInterface[]> {
    let originalActions = 0;
    let resultActions: BackupActionsInterface[] = [];
    const currentUser = localStorage.getItem('login');

    return this.dbService
      .getAll<BackupActionsInterface>('BackupActionsTable')
      .pipe(
        catchError(() => of([])),
        mergeMap(
          (actions: BackupActionsInterface[]) =>
            actions as BackupActionsInterface[]
        ),
        filter((record: BackupActionsInterface) => record.user === currentUser),
        groupBy((record: BackupActionsInterface) => record.Id),
        mergeMap(group$ =>
          group$.pipe(
            toArray(),
            tap(() => (originalActions += 1)),
            map(records =>
              records.sort(
                (a, b) =>
                  Number(b.orderBackupVersion) - Number(a.orderBackupVersion)
              )
            ),
            map(sortedRecords => sortedRecords[0]),
            tap(record => resultActions.push(record))
          )
        ),
        toArray(),
        tap(() => {
          if (originalActions !== resultActions.length) {
            resultActions = []; // Очищаем результат, если длины массивов не совпадают
          }
        }),
        map(() => resultActions),
        defaultIfEmpty([])
      );
  }

  /**
   * Обновляет версию бекапа заказа в локальном хранилище.
   *
   * @param {number} id - Идентификатор заказа.
   * @param {number} version - Новая версия бекапа.
   * @returns {Promise<boolean>} Promise, который разрешается в true, если обновление успешно, иначе false.
   */
  async updateLocalOrderBackupVersion(
    id: number,
    version: number
  ): Promise<boolean> {
    try {
      return new Promise<boolean>((resolve, reject) => {
        this.getOrderDataFromLocalStore(id)
          .pipe(
            take(1),
            catchError(error => {
              Sentry.captureException(error, {
                extra: {
                  operation: 'updateLocalOrderBackupVersion.getOrder',
                  id,
                  version
                }
              });
              reject(error);
              return of(undefined);
            })
          )
          .subscribe({
            next: (localOrder: OrderInEditStatusInterface | undefined) => {
              if (!localOrder) {
                Sentry.captureMessage(
                  `Заказ с ID ${id} не найден при обновлении версии`,
                  {
                    level: 'warning',
                    extra: { id, version }
                  }
                );
                resolve(false);
                return;
              }

              localOrder.orderBackupVersion = version;

              this.dbService
                .update(tableOrdersInEditStatus, localOrder)
                .pipe(
                  take(1),
                  catchError(error => {
                    Sentry.captureException(error, {
                      extra: {
                        operation: 'updateLocalOrderBackupVersion.update',
                        id,
                        version
                      }
                    });
                    reject(error);
                    return of(null);
                  })
                )
                .subscribe({
                  next: () => {
                    resolve(true);
                  },
                  error: error => {
                    Sentry.captureException(error, {
                      extra: {
                        operation:
                          'updateLocalOrderBackupVersion.update.subscribe',
                        id,
                        version
                      }
                    });
                    reject(error);
                  }
                });
            },
            error: error => {
              Sentry.captureException(error, {
                extra: {
                  operation: 'updateLocalOrderBackupVersion.getOrder.subscribe',
                  id,
                  version
                }
              });
              reject(error);
            }
          });
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'updateLocalOrderBackupVersion',
          id,
          version
        }
      });
      return false;
    }
  }

  /**
   * Редактирует имя, оставляя только первую букву каждого слова и заменяя остальные символы на звездочки.
   * Например: "Иван Петров" -> "И*** П*****"
   *
   * @param {string} name - Исходное имя.
   * @returns {string} Отредактированное имя.
   * @private
   */
  private redactName = (name: string): string =>
    name.replace(
      /\S+/g,
      part => part.charAt(0).toUpperCase() + '*'.repeat(part.length - 1)
    );

  /**
   * Редактирует номер телефона, оставляя код страны и последние три цифры, заменяя остальные на звездочки.
   * Например: "+79991234567" -> "+7*******567"
   *
   * @param {string} phone - Исходный номер телефона.
   * @returns {string} Отредактированный номер телефона.
   * @private
   */
  private redactPhone = (phone: string): string => {
    try {
      if (!phone) return phone;
      const digits = phone.replace(/\D/g, '');
      const lastThree = digits.length >= 3 ? digits.slice(-3) : digits;
      // Номер с ведущим '+' : показываем код страны (первую цифру) и последние три, всё между — маска.
      if (phone.trim().startsWith('+') && digits.length >= 5) {
        return `+${digits.charAt(0)}${'*'.repeat(digits.length - 4)}${lastThree}`;
      }
      // Без '+' (в т.ч. РФ-формат 8XXXXXXXXXX/7XXXXXXXXXX): маскируем всё, кроме последних трёх цифр.
      // КРИТИЧНО: прежняя реализация при непопадании в regex возвращала ПОЛНЫЙ номер — ПДн утекали
      // в openData.json (обезличенную версию). Теперь исходный номер наружу не отдаётся никогда.
      return `${'*'.repeat(Math.max(digits.length - 3, 0))}${lastThree}`;
    } catch (error) {
      Sentry.captureException(error, {
        // В лог не кладём сам номер (даже часть) — это ПДн; только длину.
        extra: {
          operation: 'redactPhone',
          phoneLength: typeof phone === 'string' ? phone.length : typeof phone
        }
      });
      return '***'; // При ошибке — плейсхолдер, НИКОГДА исходный номер.
    }
  };

  /**
   * Редактирует email-адрес, оставляя только первую букву локальной части и домен.
   * Например: "user@example.com" -> "u***@example.com"
   *
   * @param {string} email - Исходный email-адрес.
   * @returns {string} Отредактированный email-адрес.
   * @private
   */
  private redactEmail = (email: string): string => {
    try {
      if (!email) return email;
      const parts = email.split('@');
      // Некорректный формат — исходное значение наружу НЕ отдаём (иначе ПДн утекут в openData.json).
      if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return '***';
      }
      const [localPart, domain] = parts;
      const redactedLocal =
        localPart.charAt(0) + '*'.repeat(Math.max(localPart.length - 1, 0));
      return `${redactedLocal}@${domain}`;
    } catch (error) {
      Sentry.captureException(error, {
        // Сам email (даже часть) в лог не кладём — это ПДн.
        extra: { operation: 'redactEmail' }
      });
      return '***'; // При ошибке — плейсхолдер, НИКОГДА исходный email.
    }
  };

  /**
   * Добавляет действие по синхронизации бекапа в очередь для отправки на сервер.
   * Использует механизм дебаунсинга для предотвращения частых запросов к серверу.
   *
   * @param {BackupActionData} data - Данные действия для добавления в очередь.
   * @returns {Observable<boolean>} Observable, который эмитит true, если действие было отправлено на сервер,
   *                               или false, если действие было проигнорировано из-за дебаунсинга.
   * @private
   */
  private putBackupActionToQueue(data: BackupActionData): Observable<boolean> {
    if (!data || !data.order) {
      const error = new Error(
        'Попытка добавления некорректных данных в очередь'
      );
      Sentry.captureMessage(error.message, {
        level: 'warning',
        extra: { data }
      });
      return throwError(() => error);
    }

    const orderId: number = data.order.id;
    if (!orderId) {
      const error = new Error('Отсутствует ID заказа в данных для очереди');
      Sentry.captureMessage(error.message, {
        level: 'warning',
        extra: { data }
      });
      return throwError(() => error);
    }

    try {
      // Получаем или создаем Subject для данного заказа
      let orderSubject: Subject<BackupActionData> | undefined =
        this.backupActionRequestForOrder$.get(orderId);

      if (!orderSubject) {
        orderSubject = this.createBackupActionSubject();
        this.backupActionRequestForOrder$.set(orderId, orderSubject);
      }

      // Создаем Subject для результата операции
      const result: Subject<boolean> = new Subject<boolean>();
      this.backupActionResultForOrder$.set(data, result);

      // Subject результата по idempotencyKey создаём ТОЛЬКО если его ещё нет. Его мог заранее создать
      // waitForActionResult (на него уже подписан вызывающий, например модальное окно «оставить
      // локальную версию»). Перезапись новым Subject привела бы к тому, что уведомление о результате
      // (createBackupActionSubject берёт его из карты по ключу) ушло бы в новый Subject, а ожидающий
      // на старом висел бы вечно.
      if (!this.actionResultByKey$.has(data.idempotencyKey)) {
        this.actionResultByKey$.set(
          data.idempotencyKey,
          new ReplaySubject<BackupActionResult>(1)
        );
      }

      // Отправляем данные в Subject
      orderSubject.next(data);

      // Возвращаем Observable результата
      return result.asObservable();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'putBackupActionToQueue',
          orderId
        }
      });
      return throwError(() => error);
    }
  }

  /**
   * Создает Subject для обработки действий по синхронизации бекапа с дебаунсингом.
   * Реализует механизм, при котором частые запросы объединяются, и на сервер отправляется только последний.
   *
   * @returns {Subject<BackupActionData>} Subject для отправки действий по синхронизации.
   * @private
   */
  /**
   * Есть ли для заказа НЕОТПРАВЛЕННОЕ исходящее действие (SYNC_FROM_CLIENT или OVERWRITE_SERVER) текущего
   * пользователя. Защита от молчаливого затирания неотправленных правок автоматической подтяжкой
   * серверной версии. При сбое чтения таблицы действий возвращаем {@code true} (fail-safe: считаем, что
   * ожидающие есть, и подтяжку не выполняем — рисковать потерей неотправленного нельзя).
   */
  private async hasPendingOutgoingAction(id: number): Promise<boolean> {
    const email = localStorage.getItem('login');
    if (!email) {
      return false;
    }
    try {
      const records = await firstValueFrom(
        this.dbService.getAllByIndex<BackupActionsInterface>(
          'BackupActionsTable',
          'Id',
          IDBKeyRange.only(id)
        )
      );
      return (records ?? []).some(
        record =>
          record.user === email &&
          (record.backupAction === BackupActions.SYNC_FROM_CLIENT ||
            record.backupAction === BackupActions.OVERWRITE_SERVER)
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: 'hasPendingOutgoingAction', id }
      });
      return true;
    }
  }

  private createBackupActionSubject(): Subject<BackupActionData> {
    // Интервал дебаунсинга в миллисекундах
    const debounceValueMillis: number = 5000;

    // Создаем основной Subject для входящих действий
    const result: Subject<BackupActionData> = new Subject<BackupActionData>();

    try {
      // Поток для обработки действий с дебаунсингом (отправляет только последнее действие)
      const debounced = result.pipe(
        debounceTime(debounceValueMillis),
        concatMap(item =>
          // Отправляем данные на сервер
          this.writeLocalBackupToServer(
            item.order,
            item.localVersion,
            item.idempotencyKey,
            item.overwrite
          ).pipe(
            // Обрабатываем успешный ответ
            switchMap(response => {
              const ok = !!response && typeof response.status === 'number' && response.status >= 200 && response.status < 300;
              const headerVersion = ok ? Number(response.headers?.get('Backup-Version') || 0) : 0;
              const newVersion = ok ? (headerVersion > 0 ? headerVersion : item.localVersion) : undefined;

              // Обновляем локальную версию только при успехе сервера
              const updatePromise = ok && newVersion !== undefined
                ? this.updateLocalOrderBackupVersion(item.order.id, newVersion)
                : Promise.resolve(false);

              // Уведомляем старый канал результата (boolean)
              const subject = this.backupActionResultForOrder$.get(item);
              if (subject) {
                subject.next(ok);
                subject.complete();
                this.backupActionResultForOrder$.delete(item);
              }

              // Уведомляем по idempotencyKey
              const keySubject = this.actionResultByKey$.get(item.idempotencyKey);
              if (keySubject) {
                keySubject.next({ sent: true, serverOk: ok, serverVersion: newVersion });
                keySubject.complete();
                this.actionResultByKey$.delete(item.idempotencyKey);
              }

              // При ПОДТВЕРЖДЁННОЙ отправке (2xx) удаляем запись действия здесь же. Отправка идёт в
              // этом (независимом) потоке, а ожидающий в runAllBackupActions может отвалиться по
              // таймауту раньше, чем она завершится. Без удаления здесь устаревшая запись осталась бы в
              // очереди на СТАРОЙ версии (тогда как updateLocalOrderBackupVersion выше уже подняла
              // версию заказа) и на следующем прогоне ушла бы против более новой серверной → ложный 412.
              // Удаление ≤ отправленной версии убирает и опережённые excluded-записи того же заказа.
              const cleanupPromise = ok
                ? this.deleteBackupActionsByVersion(item.order.id, item.localVersion)
                : Promise.resolve(0);

              // Обновление версии и удаление записи независимы (разные хранилища: ordersInEditStatus и
              // BackupActionsTable), порядок между ними не важен — ждём оба и отдаём ответ.
              return from(Promise.all([updatePromise, cleanupPromise])).pipe(
                switchMap(() => of(response))
              );
            }),
            // Обрабатываем ошибки
            catchError(error => {
              Sentry.captureException(error, {
                extra: {
                  operation:
                    'createBackupActionSubject.writeLocalBackupToServer',
                  orderId: item.order?.id,
                  version: item.localVersion,
                  overwrite: item.overwrite
                }
              });

              const subject = this.backupActionResultForOrder$.get(item);
              if (subject) {
                subject.error(error);
                subject.complete();
                this.backupActionResultForOrder$.delete(item);
              }

              const keySubject = this.actionResultByKey$.get(item.idempotencyKey);
              if (keySubject) {
                keySubject.next({ sent: true, serverOk: false, error });
                keySubject.complete();
                this.actionResultByKey$.delete(item.idempotencyKey);
              }

              return of(null);
            })
          )
        ),
        // Завершаем поток при уничтожении сервиса
        takeUntil(this.destroy$)
      );

      // Поток для обработки пропущенных действий (все кроме последнего в интервале)
      const excluded = result.pipe(
        windowTime(debounceValueMillis),
        mergeMap(windowSubject => windowSubject.pipe(toArray())),
        filter(arr => arr.length > 1), // Фильтруем только если есть несколько действий в окне
        mergeMap(arr => arr.slice(0, -1)), // Берем все кроме последнего
        tap(item => {
          const subject = this.backupActionResultForOrder$.get(item);
          if (subject) {
            subject.next(false); // false - значит запрос проигнорировался
            subject.complete();
            this.backupActionResultForOrder$.delete(item);
          }
          const keySubject = this.actionResultByKey$.get(item.idempotencyKey);
          if (keySubject) {
            keySubject.next({ sent: false });
            keySubject.complete();
            this.actionResultByKey$.delete(item.idempotencyKey);
          }
        }),
        // Завершаем поток при уничтожении сервиса
        takeUntil(this.destroy$)
      );

      // Подписываемся на оба потока
      debounced.subscribe({
        error: () => {
          // Errors are already handled in the catchError operator
        }
      });

      excluded.subscribe({
        error: () => {
          // Errors are already handled in the catchError operator
        }
      });

      return result;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'createBackupActionSubject'
        }
      });

      // В случае ошибки возвращаем пустой Subject
      return new Subject<BackupActionData>();
    }
  }

  /**
   * Останавливает выполнение всех отложенных действий по синхронизации бекапов.
   * Завершает Subject, который используется для отмены подписки на выполнение действий.
   *
   * @private
   */
  private stopActions(): void {
    try {
      this.stopRunAllActionsSubject.next();
      this.stopRunAllActionsSubject.complete();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          operation: 'stopActions'
        }
      });
      // Даже в случае ошибки мы не выбрасываем исключение,
      // так как этот метод обычно вызывается в finalize
    }
  }
}
